import { createContext, type ParentComponent, useContext } from "solid-js";

type LayoutContextValue = {
  isMain: () => boolean;
  isSide: () => boolean;
};

const LayoutContext = createContext<LayoutContextValue>({
  isMain: () => true,
  isSide: () => false,
});

/**
 * Hook to access the current layout context.
 * Returns isMain=true, isSide=false if no context provider exists (default behavior).
 */
export const useLayout = (): LayoutContextValue => {
  const context = useContext(LayoutContext);
  // Default to main layout if no context exists
  if (!context) {
    return {
      isMain: () => true,
      isSide: () => false,
    };
  }
  return context;
};

type LayoutProviderProps = {
  /** Whether this is the main content area */
  main?: boolean;
  /** Whether this is the side panel */
  side?: boolean;
};

/**
 * Provider component that sets the layout context for children.
 * Use this to wrap content in main or side panel areas.
 */
export const LayoutProvider: ParentComponent<LayoutProviderProps> = (props) => {
  const value: LayoutContextValue = {
    isMain: () => props.main ?? false,
    isSide: () => props.side ?? false,
  };

  return <LayoutContext.Provider value={value}>{props.children}</LayoutContext.Provider>;
};
